import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { haptic } from '../../../../lib/haptic';
import { DocPicker } from '../../../../components/form/DocPicker';
import { apiFetch } from '../../../../lib/api-client';
import { useSignup } from '../SignupContext';

const fieldCardStyle: React.CSSProperties = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-card, 18px)',
  padding: '14px 16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
};

const labelStyle: React.CSSProperties = {
  fontSize: '13px',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  letterSpacing: '0.01em',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: 'none',
  outline: 'none',
  background: 'transparent',
  fontSize: '16px',
  color: 'var(--color-text)',
  padding: 0,
  fontFamily: 'inherit',
  minHeight: '24px',
};

const errorStyle: React.CSSProperties = {
  fontSize: '12px',
  color: '#B91C1C',
  margin: 0,
};

const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

export function PanStep() {
  const { t } = useTranslation('auth');
  const { state, setField } = useSignup();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const panNumber = state.panNumber;
  const panPhoto = state.panPhotoDataUrl;

  const panValid = PAN_REGEX.test(panNumber);
  // Optional for now — driver can submit KYC later. We still surface inline
  // format errors when a non-empty value doesn't match the PAN regex; empty
  // values are allowed and don't block submit.
  const isValid = panNumber.length === 0 || panValid;
  const canSubmit = isValid && !busy;

  const showPanError = panNumber.length > 0 && !panValid;

  const handleSubmit = async () => {
    if (!isValid) return;
    setBusy(true);
    setError(null);
    try {
      const payload: Record<string, string> = {};
      if (state.dlNumber)       payload.drivingLicenseNumber = state.dlNumber;
      if (state.dlExpiry)       payload.drivingLicenseExpiry = state.dlExpiry;
      if (state.dlPhotoDataUrl) payload.drivingLicenseUrl    = state.dlPhotoDataUrl;
      if (state.aadhaarNumber)       payload.aadhaarNumber = state.aadhaarNumber;
      if (state.aadhaarPhotoDataUrl) payload.aadhaarUrl    = state.aadhaarPhotoDataUrl;
      if (state.panNumber)       payload.panNumber = state.panNumber;
      if (state.panPhotoDataUrl) payload.panUrl    = state.panPhotoDataUrl;

      try {
        await apiFetch('/driver-app/me/kyc', {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
      } catch (e) {
        if ((e as { status?: number })?.status === 401) {
          navigate('/auth', { replace: true });
          return;
        }
        throw e;
      }
      haptic.success();
      navigate('/', { replace: true });
    } catch (e: unknown) {
      const errMsg = (e as { message?: string } | null | undefined)?.message;
      setError(errMsg || 'Submit failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '12px' }}>
      <div style={fieldCardStyle}>
        <label style={labelStyle} htmlFor="signup-pan-number">
          {t('signupFields.panNumber')}
        </label>
        <input
          id="signup-pan-number"
          type="text"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          value={panNumber}
          placeholder={t('signupFields.panNumberPlaceholder')}
          onChange={(e) => setField('panNumber', e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 10))}
          style={{ ...inputStyle, letterSpacing: '0.08em', fontVariantNumeric: 'tabular-nums' }}
          maxLength={10}
        />
        {showPanError && (
          <p style={errorStyle}>{t('signupValidation.panInvalid')}</p>
        )}
      </div>

      <div style={fieldCardStyle}>
        <DocPicker
          value={panPhoto || null}
          onChange={(dataUrl) => setField('panPhotoDataUrl', dataUrl ?? '')}
          label={t('signupFields.panPhoto')}
          accept="image/*,application/pdf"
        />
      </div>

      {error && (
        <p role="alert" style={{ ...errorStyle, margin: '0 0 0.5rem' }}>
          {error}
        </p>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        style={{
          marginTop: '8px',
          width: '100%',
          minHeight: 'var(--touch-target, 52px)',
          borderRadius: 'var(--radius-pill, 9999px)',
          border: 'none',
          background: canSubmit ? 'var(--color-btn-primary)' : 'var(--color-border)',
          color: canSubmit ? '#fff' : 'var(--color-text-secondary)',
          fontSize: '16px',
          fontWeight: 700,
          cursor: canSubmit ? 'pointer' : 'not-allowed',
          transition: 'background-color 150ms ease, color 150ms ease',
          fontFamily: 'inherit',
        }}
      >
        {busy ? t('signupActions.submitting') : t('signupActions.submit')}
      </button>
    </div>
  );
}
